import { UseCase } from '@magicdoor/magic-use-case-solid';
import { HttpError } from '@magicdoorinc/errors';
import { createMainAppState, type MainAppState } from '~/state/mainAppState';
import type { RentalApplicationFile } from '~/use-cases/types/RentalApplication';
import type { RentalApplicationCredentials } from '~/use-cases/types/RentalApplicationCredentials';
import type { RentalApplicationSteps } from '~/use-cases/types/RentalApplicationSteps';
import { RENTAL_APPLICATION_STEP_URLS, rentalApplicationBasePath } from '~/utils/rentalApplicationSteps';

const NOT_A_NUMBER = /[^0-9.-]+/g;

export abstract class MagicUseCase<TParams = void> extends UseCase<MainAppState, TParams> {
  protected async initializeState(): Promise<MainAppState> {
    return createMainAppState();
  }

  protected errorCodeOf(error: HttpError): string | undefined {
    return error.getJson<{ errorCode?: string }>()?.errorCode?.toLowerCase();
  }

  protected convertToFloat(value?: string): number | undefined {
    if (!value) return undefined;
    const result = parseFloat(value.replace(NOT_A_NUMBER, ''));
    return isNaN(result) ? undefined : result;
  }

  protected loadedFiles(): RentalApplicationFile[] {
    const application = this.getState().user.rentalApplication.application;
    const now = new Date();
    return [
      ...(application.files ?? []),
      ...(application.employmentHistory ?? []).flatMap((employment) => employment.files ?? []),
      ...(application.residentialHistory ?? []).flatMap((residence) => residence.files ?? []),
    ].flatMap((file) => {
      const publicUrl = file.publicUrl;
      if (!publicUrl || (publicUrl.expires && new Date(publicUrl.expires) <= now)) return [];
      return [{ ...file, publicUrl: { ...publicUrl }, urls: file.urls && { ...file.urls } }];
    });
  }

  protected stepPath(credentials: RentalApplicationCredentials, step: RentalApplicationSteps): string {
    return `${rentalApplicationBasePath(credentials)}${RENTAL_APPLICATION_STEP_URLS[step]}`;
  }
}
